import { useEffect, useRef, useState } from "react";
import { Camera, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";

export default function ProfilePhotoInput({
  value,
  onChange,
  label = "Profile Photo",
  description = "File se upload karein ya camera se live photo lein.",
  className = "",
}) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const fileRef = useRef(null);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);

  const stopCamera = () => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
    setCameraLoading(false);
    setCameraReady(false);
  };

  const startCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Camera not available in this browser");
      return;
    }
    try {
      setCameraLoading(true);
      setCameraReady(false);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraOpen(true);
    } catch {
      toast.error("Camera permission denied or unavailable");
    } finally {
      setCameraLoading(false);
    }
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;
    if (!cameraReady || video.readyState < 2 || !video.videoWidth || !video.videoHeight) {
      toast.error("Camera loading hai, 1 second wait karke capture karein");
      return;
    }
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    onChange(canvas.toDataURL("image/jpeg", 0.85));
    stopCamera();
    toast.success("Profile photo captured");
  };

  const uploadPhoto = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please select an image file");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => onChange(String(reader.result || ""));
    reader.onerror = () => toast.error("Failed to read image");
    reader.readAsDataURL(file);
    event.target.value = "";
  };

  useEffect(() => {
    const video = videoRef.current;
    const stream = streamRef.current;
    if (!cameraOpen || !video || !stream) return;

    video.srcObject = stream;
    video.play().then(() => {
      if (video.videoWidth && video.videoHeight) setCameraReady(true);
    }).catch(() => toast.error("Camera preview start nahi ho paya"));
  }, [cameraOpen]);

  useEffect(() => () => stopCamera(), []);

  return (
    <div className={`space-y-3 rounded-lg border border-gray-200 p-3 ${className}`}>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <Label>{label}</Label>
          <p className="text-xs text-hospital-text-muted">{description}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" onClick={() => fileRef.current?.click()}>
            <Upload className="mr-2 h-4 w-4" />
            Upload
          </Button>
          <Button type="button" variant="outline" onClick={startCamera} disabled={cameraLoading}>
            <Camera className="mr-2 h-4 w-4" />
            {cameraLoading ? "Opening..." : "Camera"}
          </Button>
          {value && (
            <Button type="button" variant="outline" onClick={() => onChange("")}>
              <X className="mr-2 h-4 w-4" />
              Remove
            </Button>
          )}
        </div>
        <Input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} />
      </div>

      {value && (
        <img src={value} alt={label} className="h-28 w-28 rounded-lg border border-gray-200 object-cover" />
      )}

      {cameraOpen && (
        <div className="space-y-3 rounded-lg bg-slate-50 p-3">
          <video
            ref={videoRef}
            className="max-h-72 w-full rounded-lg bg-black object-contain"
            autoPlay
            playsInline
            muted
            onLoadedMetadata={() => setCameraReady(true)}
            onCanPlay={() => setCameraReady(true)}
          />
          <canvas ref={canvasRef} className="hidden" />
          <p className="text-xs text-hospital-text-muted">
            {cameraReady ? "Camera ready hai. Ab photo capture kar sakte hain." : "Camera start ho raha hai..."}
          </p>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={stopCamera}>Close Camera</Button>
            <Button type="button" className="btn-primary" onClick={capturePhoto} disabled={!cameraReady}>
              Capture Photo
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
